/**
 * Oversight's verification checklist for an incident (13.2-06; D-13.2-A-1, D-13.2-P-8): the single definition of
 * "verified" that Verify's "Incidents to close" (13.2-08) and `approveAndClose`'s `openChecks` gate (13.2-05) share.
 *
 * Six checks, each `{ key, label, ok, detail }`:
 *   - `decision-made`: the decider has made the reportability call (not blank, not `Pending`); the CM's draft is named;
 *   - `notices-recorded`: every non-conditional notice `noticeDeadlines` gives for the stored decision, `AwareAt`, class
 *     and WorkSafe answer is recorded. A reportable call with no `AwareAt` fails closed (the 13-29 lens rule);
 *   - `final-report`: `FinalReportRequired = Yes` ⇒ `FinalReportGivenAt` is set;
 *   - `hrwhs-joined`: `HrWhsTriageNeeded = Yes` or `WorkerInjured = Yes` ⇒ `WorkSafeNotifiable` is answered;
 *   - `follow-ups-current`: no follow-up is overdue or undated ("done or scheduled", D-13.2-A-1);
 *   - `worker-update`: `WorkerInjured = Yes` ⇒ a worker-update follow-up exists that is done or scheduled (D-13.2-A-3).
 *
 * Deadlines come from `noticeDeadlines` / `notificationDue` only: no second deadline table. Choice values go through
 * `liveChoice`. `ReportabilityDraft` and `HrWhsTriageNeeded` (13.2-01's new columns) are read as plain text, so this
 * module loads before they are live.
 *
 * Pure: no Graph, no React, no `Date.now()`.
 */

import { liveChoice } from '../config/fieldset/live';
import type { RecordRow } from '../types/records';
import { formatDay, formatInstant } from './fieldValues';
import { FOLLOW_UP_ACTION_TYPES, type FollowUp } from './followUps';
import { noticeDeadlines, notificationDue, type DeadlineRule, type RuleKey } from './notificationDeadlines';

// ---------------------------------------------------------------------------
// Vocabulary
// ---------------------------------------------------------------------------

export const CHECK_KEYS = [
  'decision-made',
  'notices-recorded',
  'final-report',
  'hrwhs-joined',
  'follow-ups-current',
  'worker-update',
] as const;

export type CheckKey = (typeof CHECK_KEYS)[number];

/** The wording Verify shows, and the text `openChecks` hands the approve gate. */
export const CHECK_LABELS: Readonly<Record<CheckKey, string>> = {
  'decision-made': 'The decider has made the reportability call',
  'notices-recorded': 'Every notice owed is recorded',
  'final-report': 'The final report is given, if one is required',
  'hrwhs-joined': 'HR & WHS have answered the WorkSafe question',
  'follow-ups-current': 'Every follow-up is done or scheduled',
  'worker-update': 'The injured worker has an update done or scheduled',
};

export interface CheckItem {
  key: CheckKey;
  label: string;
  ok: boolean;
  detail: string;
}

const [PENDING] = liveChoice('incidents', 'ReportabilityDecision', 'Pending');
const [YES] = liveChoice('incidents', 'WorkerInjured', 'Yes');
const [FINAL_YES] = liveChoice('incidents', 'FinalReportRequired', 'Yes');

/** How each notice reads in a detail line. */
const NOTICE_LABEL: Readonly<Partial<Record<RuleKey, string>>> = {
  'sirs-p1': 'SIRS Priority 1 notice',
  'sirs-p2': 'SIRS Priority 2 notice',
  'ndis-24h': 'NDIS 24-hour notice',
  'ndis-24h-further': 'NDIS 5-day follow-up notice',
  'ndis-5bd': 'NDIS 5-business-day notice',
  worksafe: 'WorkSafe notice',
  'worksafe-written-record': 'WorkSafe written record',
};

/** An instant the rule selection is probed with when `AwareAt` is missing (the due times are not used). */
const PROBE_AWARE_AT = '2000-01-01T00:00:00Z';

const text = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');
const blank = (v: unknown): boolean => text(v) === '';

function check(key: CheckKey, ok: boolean, detail: string): CheckItem {
  return { key, label: CHECK_LABELS[key], ok, detail };
}

// ---------------------------------------------------------------------------
// The checks
// ---------------------------------------------------------------------------

function decisionMade(row: RecordRow): CheckItem {
  const decision = text(row.fields.ReportabilityDecision);
  if (decision !== '' && decision !== PENDING) return check('decision-made', true, `Decided: ${decision}.`);
  const draft = text(row.fields.ReportabilityDraft);
  const note = text(row.fields.ReportabilityDraftNote);
  const draftLine = draft
    ? `The Care Manager's draft is ${draft}${note ? ` ("${note}")` : ''}; the call stays with the decider.`
    : 'There is no draft from the Care Manager yet.';
  return check('decision-made', false, `Waiting on the decider's call. ${draftLine}`);
}

/** `<label> (<column>)`, with its due time when known (and "passed" once `now` is beyond it). */
function noticeLine(rule: DeadlineRule, now: Date | null): string {
  const label = `${NOTICE_LABEL[rule.key] ?? 'Notice'} (${rule.notice})`;
  if (now === null) return label;
  const passed = Date.parse(rule.dueAt) < now.getTime() ? ', passed' : '';
  return `${label}, due ${formatInstant(rule.dueAt)}${passed}`;
}

function noticesRecorded(row: RecordRow, now: Date): CheckItem {
  const input = {
    awareAt: text(row.fields.AwareAt) || null,
    decision: text(row.fields.ReportabilityDecision) || null,
    ndisNoticeClass: text(row.fields.NdisNoticeClass) || null,
    workSafeNotifiable: text(row.fields.WorkSafeNotifiable) || null,
    notices: row.fields,
  };
  const owed = (rules: DeadlineRule[]) => rules.filter((r) => r.notice && !r.conditional && blank(row.fields[r.notice]));

  if (notificationDue(input).missing === 'aware-at') {
    // Fail closed: a reportable call with no AwareAt still owes its notices; the probe names which.
    const missing = owed(noticeDeadlines({ ...input, awareAt: PROBE_AWARE_AT }));
    const named = missing.length ? ` Not recorded: ${missing.map((r) => noticeLine(r, null)).join('; ')}.` : '';
    return check('notices-recorded', false, `AwareAt is not recorded, so the notice deadlines can't be worked out.${named}`);
  }

  const rules = noticeDeadlines(input);
  const missing = owed(rules);
  if (missing.length > 0) {
    return check('notices-recorded', false, `Not recorded: ${missing.map((r) => noticeLine(r, now)).join('; ')}.`);
  }
  const required = rules.filter((r) => r.notice && !r.conditional).length;
  return check('notices-recorded', true, required === 0 ? 'No notice is owed.' : `All ${required} notice(s) owed are recorded.`);
}

function finalReport(row: RecordRow): CheckItem {
  if (text(row.fields.FinalReportRequired) !== FINAL_YES) return check('final-report', true, 'No final report is required.');
  if (!blank(row.fields.FinalReportGivenAt)) {
    return check('final-report', true, `Given ${formatInstant(text(row.fields.FinalReportGivenAt))}.`);
  }
  const dueOn = text(row.fields.FinalReportDueOn);
  return check('final-report', false, `A final report is required and not recorded (FinalReportGivenAt)${dueOn ? `, due ${formatDay(dueOn)}` : ''}.`);
}

function hrwhsJoined(row: RecordRow): CheckItem {
  const injured = text(row.fields.WorkerInjured) === YES;
  const asked = text(row.fields.HrWhsTriageNeeded) === YES;
  if (!injured && !asked) return check('hrwhs-joined', true, 'HR & WHS were not needed.');
  const why = injured ? 'A worker was injured' : 'The Care Manager asked HR & WHS to join';
  const answer = text(row.fields.WorkSafeNotifiable);
  return answer
    ? check('hrwhs-joined', true, `${why}; WorkSafe notifiable: ${answer}.`)
    : check('hrwhs-joined', false, `${why}, and HR & WHS have not answered WorkSafe notifiable yet.`);
}

function followUpLine(f: FollowUp): string {
  return f.state === 'undated' ? `${f.row.reference} has no due date` : `${f.row.reference} overdue (due ${formatDay(f.dueOn)})`;
}

function followUpsCurrent(followUps: readonly FollowUp[]): CheckItem {
  const late = followUps.filter((f) => f.state === 'overdue' || f.state === 'undated');
  if (late.length > 0) return check('follow-ups-current', false, `${late.map(followUpLine).join('; ')}.`);
  if (followUps.length === 0) return check('follow-ups-current', true, 'No follow-ups.');
  const done = followUps.filter((f) => f.state === 'done').length;
  const scheduled = followUps.filter((f) => f.state === 'scheduled').length;
  return check('follow-ups-current', true, `${done} done, ${scheduled} scheduled.`);
}

function workerUpdate(row: RecordRow, followUps: readonly FollowUp[]): CheckItem {
  if (text(row.fields.WorkerInjured) !== YES) return check('worker-update', true, 'No worker was injured.');
  const updates = followUps.filter((f) => f.row.actionType === FOLLOW_UP_ACTION_TYPES['worker-update']);
  const current = updates.find((f) => f.state === 'done' || f.state === 'scheduled');
  if (current) return check('worker-update', true, `${current.row.reference} is ${current.state}.`);
  return check(
    'worker-update',
    false,
    updates.length > 0
      ? `The worker update is not done or scheduled: ${updates.map(followUpLine).join('; ')}.`
      : 'A worker was injured and no worker-update follow-up is recorded.',
  );
}

// ---------------------------------------------------------------------------
// The API
// ---------------------------------------------------------------------------

/**
 * The checklist for `row`, given its follow-ups (from `followUpsOf(row.ref, arRows, now)`, the same `now`). `now`
 * marks a missing notice whose due time has passed.
 */
export function incidentChecklist(row: RecordRow, followUps: readonly FollowUp[], now: Date): CheckItem[] {
  return [
    decisionMade(row),
    noticesRecorded(row, now),
    finalReport(row),
    hrwhsJoined(row),
    followUpsCurrent(followUps),
    workerUpdate(row, followUps),
  ];
}

/** The labels of the checks that are not ok: what `approveAndClose` refuses on (13.2-05). */
export function openChecks(items: readonly CheckItem[]): string[] {
  return items.filter((i) => !i.ok).map((i) => i.label);
}
